import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { PanneauControles, useCorpus } from "./PanneauControles";
import { aJuger, controler } from "./controles";
import { oublierCorpus } from "./corpus";
import { messageErreur } from "./libelles";
import { ErreurBase, appeler } from "./supabase";
import type { Entree } from "./types";

const decouper = (texte: string) =>
  texte
    .split(",")
    .map((v) => v.trim())
    .filter((v) => v.length > 0);

/**
 * Corriger une entrée ou en créer une. Une entrée publiée reste publiée : la
 * correction doit donc passer les contrôles, et ses signaux de facilité être
 * confirmés. Un brouillon s'enregistre tel quel ; il sera jugé à la publication.
 */
export function Editeur(props: {
  readonly entree: Entree | null;
  readonly onEnregistre: (id: string) => void;
  readonly onAnnuler: () => void;
}) {
  const { entree } = props;
  const [mot, setMot] = useState(entree?.mot ?? "");
  const [definition, setDefinition] = useState(entree?.definition ?? "");
  const [variantes, setVariantes] = useState(entree?.variantes.join(", ") ?? "");
  const [difficulte, setDifficulte] = useState(entree?.difficulte ?? 3);
  const [note, setNote] = useState(entree?.note_culturelle ?? "");
  const [motif, setMotif] = useState("");
  const [confirme, setConfirme] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const corpus = useCorpus();

  const publiee = entree?.statut === "approuvee";
  const controles = useMemo(
    () => controler({ id: entree?.id ?? "nouvelle", mot, definition, variantes: decouper(variantes) }, corpus),
    [entree?.id, mot, definition, variantes, corpus],
  );

  // Écrire dans une entrée que les joueurs voient exige qu'elle tienne les
  // règles et que les signaux aient été vus. Un brouillon, non.
  const bloque =
    envoi ||
    mot.trim() === "" ||
    definition.trim() === "" ||
    (entree !== null && motif.trim() === "") ||
    (publiee && (controles.echecs.length > 0 || (aJuger(controles) && !confirme)));

  const enregistrer = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setEnvoi(true);
    setErreur(null);
    try {
      const resultat = await appeler<Entree>("admin_enregistrer", {
        p_id: entree?.id ?? null,
        p_mot: mot,
        p_definition: definition,
        p_difficulte: difficulte,
        p_variantes: decouper(variantes),
        p_note_culturelle: note.trim() === "" ? null : note,
        p_motif: motif.trim() === "" ? null : motif,
        p_facilite_vue: confirme,
      });
      oublierCorpus();
      props.onEnregistre(resultat.id);
    } catch (e) {
      setErreur(messageErreur(e instanceof ErreurBase ? e.code : "erreur_inconnue"));
      setEnvoi(false);
    }
  };

  return (
    <form onSubmit={(e) => void enregistrer(e)} className="editeur" aria-label={entree === null ? "Nouvelle entrée" : "Correction"}>
      <div className="colonnes">
        <div className="pile">
          <label>
            Mot
            <input
              value={mot}
              onChange={(e) => setMot(e.target.value)}
              readOnly={publiee}
              aria-describedby={publiee ? "mot-fige" : undefined}
              required
            />
          </label>
          {publiee && (
            <p id="mot-fige" className="discret">
              Le mot d'une entrée publiée ne change pas : retire-la et crée-en une autre.
            </p>
          )}
          <label>
            Définition
            <textarea rows={3} value={definition} onChange={(e) => setDefinition(e.target.value)} required />
          </label>
          <label>
            Variantes acceptées, séparées par des virgules
            <input value={variantes} onChange={(e) => setVariantes(e.target.value)} />
          </label>
          <label>
            Difficulté (1 à 5)
            <select value={difficulte} onChange={(e) => setDifficulte(Number(e.target.value))}>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <label>
            Note culturelle (montrée au résultat, facultative)
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          {entree !== null && (
            <label>
              Motif de la correction
              <input value={motif} onChange={(e) => setMotif(e.target.value)} required placeholder="Pourquoi ce changement ?" />
            </label>
          )}
        </div>
        <PanneauControles controles={controles} corpusCharge={corpus !== null} confirme={confirme} onConfirme={setConfirme} />
      </div>
      {!publiee && controles.echecs.length > 0 && (
        <p className="discret">Un brouillon s'enregistre tel quel ; il ne se publiera qu'une fois ces règles respectées.</p>
      )}
      {erreur !== null && (
        <p role="alert" className="erreur">
          {erreur}
        </p>
      )}
      <div className="actions">
        <button type="submit" className="principal" disabled={bloque}>
          {entree === null ? "Enregistrer le brouillon" : "Enregistrer la correction"}
        </button>
        <button type="button" onClick={props.onAnnuler}>
          Annuler
        </button>
      </div>
    </form>
  );
}
